import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const TermsPage = () => {
  useEffect(() => {
    document.title = "Terms of Service - Hyperapt";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Terms of Service for Hyperapt");
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Terms of Service for Hyperapt";
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-web-bg text-web-text font-web-sans flex flex-col selection:bg-web-primary selection:text-white">
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md transition-all duration-300 h-20 flex items-center border-b border-web-border/50">
        <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-center">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto" />
            <span className="font-web-serif font-semibold text-[36px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 pt-32 pb-24">
        
        <div className="text-center mb-12">
          <h1 className="font-web-serif text-4xl lg:text-5xl font-bold text-web-text mb-4">Terms of Service</h1>
          <p className="text-web-text/60 text-lg font-light">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>

        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-web-border/60">
          
          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">1. Introduction</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                This document is an electronic record in terms of the Information Technology Act, 2000 and rules there under as applicable and the amended provisions pertaining to electronic records. This electronic record is generated by a computer system and does not require any physical or digital signatures.
              </p>
              <p>
                The domain name and application "Hyperapt" (hereinafter referred to as the "Platform") are owned and operated by HYPERAPT INDIA PRIVATE LIMITED, a company incorporated under the Companies Act, 1956/2013 with its registered office in India.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">2. Nature of the Platform</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p className="font-medium text-web-text">
                Hyperapt acts solely as a Technology Intermediary and Digital Platform.
              </p>
              <p>
                We are a digital prescription and receipt generation platform utilized by registered medical practitioners (Doctors) to seamlessly generate digital prescriptions and consultation receipts for their patients.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>No Inventory:</strong> We strictly do not store, possess, or manage any inventory of pharmacies, medicines, drugs, or healthcare products.</li>
                <li><strong>No Direct Sale:</strong> Hyperapt does not directly sell or dispense any medicines, drugs, or medical equipment to patients or any other users.</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">3. Role of Third-Party Pharmacies</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                Our platform provides a facility to forward the digital prescriptions generated by the Doctor to licensed third-party pharmacies, strictly upon the consent of the patient.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Order Fulfillment:</strong> Upon receiving a prescription via our platform, the respective third-party pharmacy is solely responsible for verifying the prescription, generating the respective order, and dispensing the medicines.</li>
                <li><strong>Invoicing:</strong> All invoices for medicines and drugs are generated directly by the third-party pharmacies in their own name. These invoices are the property, responsibility, and legal liability of the respective third-party pharmacy, not Hyperapt.</li>
                <li><strong>Delivery:</strong> While our delivery partners may facilitate the transport of medicines from the third-party pharmacy to the patient, the actual sale transaction occurs exclusively between the patient and the third-party pharmacy.</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">4. Medical Disclaimer</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                Hyperapt is not a medical provider and does not offer medical advice, diagnosis, or treatment. The relationship between you and the consulting doctor is strictly an independent doctor-patient relationship. Hyperapt holds no liability for the medical advice, prescription accuracy, or treatment outcomes provided by the doctors using our platform.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">5. Limitation of Liability</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                Since Hyperapt is purely a technology facilitator, we shall not be held liable for:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Any adverse effects, efficacy, or quality of the medicines dispensed by third-party pharmacies.</li>
                <li>Any delays or failures in order fulfillment by the third-party pharmacies.</li>
                <li>Any errors or omissions in the digital prescription generated by the medical practitioner.</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">6. Governing Law & Jurisdiction</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                These Terms of Service shall be governed by and constructed in accordance with the laws of India. Any disputes arising in relation hereto shall be subject to the exclusive jurisdiction of the courts located in Maharashtra, India.
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-web-bg text-web-text py-8 border-t border-web-border mt-auto relative z-30">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto opacity-90" />
            <span className="font-web-serif font-semibold text-[32px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>

          <nav className="flex flex-wrap justify-center gap-6 text-sm text-web-text/70 font-light">
            <Link to="/trackorder" className="hover:text-web-primary transition-colors">Track Order</Link>
            <Link to="/partner" className="hover:text-web-primary transition-colors">For Doctors</Link>
            <Link to="/terms" className="hover:text-web-primary transition-colors">Terms of Service</Link>
            <Link to="/privacypolicy" className="hover:text-web-primary transition-colors">Privacy Policy</Link>
            <Link to="/returnpolicy" className="hover:text-web-primary transition-colors">Return Policy</Link>
          </nav>

          <div className="text-sm text-web-text/50 font-light">
            © {new Date().getFullYear()} Hyperapt. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
